import React, { useState, useEffect, useCallback } from "react";
import Card from "../components/common/card";
import Button from "../components/common/button";
import Badge from "../components/common/badge";
import Input from "../components/common/input";
import SubscriptionTable from "../components/common/subscriptions-table";
import RestaurantSelectModal from "../components/common/RestaurantSelectModal";
import moment from "moment";
import authService from "../services/authService";
import { SubscriptionService } from "../services/subscriptionService";
import { ManageStaffUserService } from "../services/ManageStaffUsers";
import { getRestaurants } from "../services/lessonProgress";
import Modal from "../components/common/modal";
import { useStripe } from "@stripe/react-stripe-js";
import { Snackbar, Alert } from "@mui/material";

// Issue 37: subscription/billing is scoped to the selected restaurant's
// organization, not to the logged-in user. Any staff member assigned to the
// restaurant can view this page; only the Account Owner and explicitly
// granted Billing Admins can change or cancel the subscription or manage who
// else is a Billing Admin (callerCanManageBilling, from the backend). Any
// Director/Manager may start the *first* subscription when none exists yet
// (callerCanStartFirstSubscription).

const calculateMultiLocationPrice = (locationCount) => {
  if (locationCount <= 2) return 129.99;
  return 129.99 + (locationCount - 2) * 50;
};

/** A modal that requires the caller's current password before an action runs - the
 * backend independently re-verifies it, this just avoids a round-trip for a typo. */
function ReauthModal({ isOpen, title, description, confirmLabel, confirmClassName, onCancel, onConfirm, submitting }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setError("");
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <div className="p-6">
        <h2 className="text-lg font-semibold mb-2">{title}</h2>
        {description && <p className="text-gray-700 mb-4">{description}</p>}
        <Input
          label="Confirm your current password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Current password"
          error={error}
        />
        <div className="flex gap-2 mt-5">
          <Button
            className={`flex-1 text-white ${confirmClassName || "bg-primary"}`}
            disabled={submitting}
            onClick={() => {
              if (!password) {
                setError("Password is required.");
                return;
              }
              onConfirm(password);
            }}
          >
            {submitting ? "Please wait..." : confirmLabel}
          </Button>
          <Button className="bg-gray-300 flex-1" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function ManageSubscription() {
  const stripe = useStripe();

  const [restaurants, setRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [isRestaurantModalOpen, setIsRestaurantModalOpen] = useState(false);

  const [userRole, setUserRole] = useState("");

  const [subData, setSubData] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);

  const [plans, setPlans] = useState([]);
  const [multiLocationModalOpen, setMultiLocationModalOpen] = useState(false);
  const [multiLocationCount, setMultiLocationCount] = useState(2);

  const [transactions, setTransactions] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [pendingAction, setPendingAction] = useState(null); // { type: 'cancel' | 'changePlan' | 'grant' | 'revoke', ...args }
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const [eligibleStaff, setEligibleStaff] = useState([]);
  const [addAdminTarget, setAddAdminTarget] = useState("");

  const notify = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser?.role) setUserRole(currentUser.role);

    const fetchRestaurants = async () => {
      setRestaurantsLoading(true);
      try {
        const data = await getRestaurants("name");
        const arr = Array.isArray(data) ? data : [];
        setRestaurants(arr);
        if (arr.length > 0) setSelectedRestaurant(arr[0].uuid);
      } catch (error) {
        console.error("Error fetching restaurants:", error);
        setRestaurants([]);
      } finally {
        setRestaurantsLoading(false);
      }
    };
    fetchRestaurants();

    const fetchPlans = async () => {
      try {
        setPlans(await SubscriptionService.getPlans());
      } catch (error) {
        console.log(error, "error");
      }
    };
    fetchPlans();
  }, []);

  const refreshSubscription = useCallback(async () => {
    if (!selectedRestaurant) return;
    setLoadingSub(true);
    try {
      const data = await SubscriptionService.getOrganizationSubscription(selectedRestaurant);
      setSubData(data);
    } catch (error) {
      console.error("Error fetching organization subscription:", error);
      setSubData(null);
    } finally {
      setLoadingSub(false);
    }
  }, [selectedRestaurant]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  useEffect(() => {
    if (userRole !== "super_admin") return;
    const fetchAllTransactions = async () => {
      try {
        setTransactions(await SubscriptionService.getAllTransactions());
      } catch (error) {
        console.log(error, "error");
      }
    };
    fetchAllTransactions();
  }, [userRole]);

  // Eligible directors/managers at this restaurant, for the "add Billing Admin" picker.
  useEffect(() => {
    if (!selectedRestaurant || !subData?.callerCanManageBilling) {
      setEligibleStaff([]);
      return;
    }
    const fetchStaff = async () => {
      try {
        const [directors, managers] = await Promise.all([
          ManageStaffUserService.getAllStaffUsers({ restaurant: selectedRestaurant, role: "director", limit: 100 }),
          ManageStaffUserService.getAllStaffUsers({ restaurant: selectedRestaurant, role: "manager", limit: 100 }),
        ]);
        const currentAdmins = new Set((subData?.billingAdmins || []).map((a) => a.uuid));
        const combined = [...(directors?.data || []), ...(managers?.data || [])].filter(
          (u) => !currentAdmins.has(u.uuid)
        );
        setEligibleStaff(combined);
      } catch (error) {
        console.error("Error fetching eligible staff:", error);
        setEligibleStaff([]);
      }
    };
    fetchStaff();
  }, [selectedRestaurant, subData]);

  const freePlan = plans.find((p) => p.unit_amount === 0 && p?.product?.name === "Free trial");
  const singleLocationPlan = plans.find((p) => p.unit_amount === 7999);
  const multiLocationPlan = plans.find((p) => p?.product?.name === "Multi Location");

  const handleStartCheckout = async (planId, amountDollars, isFreeTrial) => {
    if (!stripe) {
      notify("Stripe is not loaded yet.", "error");
      return;
    }
    try {
      const session = await SubscriptionService.createCheckoutSession(
        selectedRestaurant,
        planId,
        1,
        amountDollars,
        isFreeTrial,
        "subscription"
      );
      if (session?.id) {
        const { error } = await stripe.redirectToCheckout({ sessionId: session.id });
        if (error) notify(error.message || "Stripe checkout failed.", "error");
      } else {
        notify("Failed to create Stripe session.", "error");
      }
    } catch (err) {
      notify(err?.response?.data?.error || "Error starting checkout.", "error");
    }
  };

  const runPendingAction = async (currentPassword) => {
    if (!pendingAction) return;
    setActionSubmitting(true);
    try {
      if (pendingAction.type === "cancel") {
        await SubscriptionService.cancelSubscription(selectedRestaurant, currentPassword, pendingAction.immediate);
        notify(
          pendingAction.immediate
            ? "Subscription canceled immediately."
            : "Subscription set to cancel at end of period."
        );
      } else if (pendingAction.type === "changePlan") {
        await SubscriptionService.changeSubscriptionPlan(
          selectedRestaurant,
          currentPassword,
          pendingAction.planId,
          pendingAction.amountDollars
        );
        notify("Subscription plan updated successfully.");
      } else if (pendingAction.type === "grant") {
        await SubscriptionService.grantBillingAdmin(selectedRestaurant, currentPassword, pendingAction.targetUserUuid);
        notify("Billing Admin added.");
      } else if (pendingAction.type === "revoke") {
        await SubscriptionService.revokeBillingAdmin(selectedRestaurant, currentPassword, pendingAction.targetUserUuid);
        notify("Billing Admin removed.");
      }
      setPendingAction(null);
      await refreshSubscription();
    } catch (error) {
      notify(error?.response?.data?.message || error?.response?.data?.error || "Action failed.", "error");
    } finally {
      setActionSubmitting(false);
    }
  };

  if (restaurantsLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
      </div>
    );
  }

  const organization = subData?.organization;
  const subscription = organization?.subscription;
  const hasActiveSubscription = subData?.hasSubscription;
  const canManageBilling = Boolean(subData?.callerCanManageBilling);
  const canStartFirstSubscription = Boolean(subData?.callerCanStartFirstSubscription);
  const endDate = subscription?.current_period_end ? moment.utc(subscription.current_period_end) : null;
  const remainingDays = endDate ? endDate.diff(moment.utc(), "days") : null;

  return (
    <div className="max-w-7xl mx-auto p-2 md:p-6 w-full">
      {/* Restaurant selector */}
      <div className="mb-6 flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-left">
          <div className="text-xs text-gray-500">Managing subscription for</div>
          <div className="font-semibold text-lg">
            {restaurants.find((r) => r.uuid === selectedRestaurant)?.name || "Select a restaurant"}
          </div>
        </div>
        {restaurants.length > 1 && (
          <Button className="bg-gray-100 text-gray-800" onClick={() => setIsRestaurantModalOpen(true)}>
            Change Restaurant
          </Button>
        )}
      </div>

      {loadingSub ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-200 border-t-red-500"></div>
        </div>
      ) : !hasActiveSubscription ? (
        <>
          {!canStartFirstSubscription && userRole !== "super_admin" && (
            <div className="mb-6 bg-[#FFF7E8] border border-[#F4C56A] rounded-lg px-4 py-3 text-left">
              <span className="text-sm text-[#B54708]">
                This organization is currently on Free Trial. Only a Director or Manager assigned to this restaurant
                can start a subscription.
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-gray-200 rounded-lg shadow-card p-6 flex flex-col justify-between">
              <div>
                <div className="text-lg font-semibold mb-2">Free</div>
                <div className="text-2xl font-bold mb-2">$0</div>
                <ul className="text-sm text-gray-700 mb-4 list-disc pl-5 space-y-1">
                  <li>1 location</li>
                  <li>Basic menu management</li>
                  <li>Limited staff training</li>
                  <li>Only Unit 1 is unlocked</li>
                </ul>
              </div>
              <Button className="bg-gray-300 w-full mt-2">You're on a free trial</Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-card p-6 flex flex-col justify-between">
              <div>
                <div className="text-lg font-semibold mb-2">Single Location</div>
                <div className="text-2xl font-bold mb-2">
                  $79.99<span className="text-base font-normal">/month</span>
                </div>
                <ul className="text-sm text-gray-700 mb-4 list-disc pl-5 space-y-1">
                  <li>1 location</li>
                  <li>Full menu management</li>
                  <li>Staff training access</li>
                  <li>Email support</li>
                </ul>
              </div>
              <Button
                className="bg-primary text-white w-full mt-2"
                disabled={!canStartFirstSubscription && userRole !== "super_admin"}
                onClick={() =>
                  singleLocationPlan &&
                  handleStartCheckout(singleLocationPlan.id, singleLocationPlan?.amount_decimal, false)
                }
              >
                Subscribe
              </Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg shadow-card p-6 flex flex-col justify-between">
              <div>
                <div className="text-lg font-semibold mb-2">Multi-Location</div>
                <div className="text-2xl font-bold mb-2">
                  ${calculateMultiLocationPrice(multiLocationCount).toFixed(2)}
                  <span className="text-base font-normal">/month</span>
                </div>
                <ul className="text-sm text-gray-700 mb-4 list-disc pl-5 space-y-1">
                  <li>Custom number of locations</li>
                  <li>Full menu management</li>
                  <li>Staff training access</li>
                  <li>24/7 support</li>
                </ul>
              </div>
              <Button
                className="bg-primary text-white w-full mt-2"
                disabled={!canStartFirstSubscription && userRole !== "super_admin"}
                onClick={() => setMultiLocationModalOpen(true)}
              >
                Subscribe
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {subscription?.cancel_at_period_end && (
            <div className="flex items-stretch mb-4">
              <div className="w-1.5 rounded-l-md bg-[#C1121F]" />
              <div className="flex-1 bg-[#FDECEC] border border-[#F9CFCF] rounded-r-md px-4 py-3 text-left">
                <span className="text-sm text-[#C1121F]">
                  This subscription is set to cancel {endDate ? `on ${endDate.format("MMM D, YYYY")}` : "at the end of the current period"}
                  {remainingDays !== null ? ` (${remainingDays} days left)` : ""}.
                </span>
              </div>
            </div>
          )}

          {!canManageBilling && (
            <div className="mb-4 bg-[#F0F6FF] border border-[#BFD9FF] rounded-lg px-4 py-3 text-left">
              <span className="text-sm text-[#1E4FA0]">
                You can view this organization's subscription. Only the Account Owner or a Billing Admin can change
                or cancel it.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white border text-left border-gray-200 rounded-lg shadow-card p-4">
              <div className="text-xs text-gray-500 mb-1">Current Plan</div>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-lg">{subscription?.plan}</div>
                <Badge
                  variant={subscription?.status === "active" ? "warning" : "danger"}
                  className={subscription?.status === "active" ? "!bg-[#FFE066] !text-gray-900" : ""}
                >
                  {subscription?.status}
                </Badge>
              </div>
            </div>
            <div className="bg-white text-left border border-gray-200 rounded-lg shadow-card p-4">
              <div className="text-xs text-gray-500 mb-1">Billing Cycle</div>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-lg">Monthly</div>
                <span className="text-base font-normal">${subscription?.amount}/month</span>
              </div>
            </div>
            <div className="bg-white text-left border border-gray-200 rounded-lg shadow-card p-4">
              <div className="text-xs text-gray-500 mb-1">
                {subscription?.cancel_at_period_end ? "Access Ends" : "Next Billing Date"}
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-lg">{endDate ? endDate.format("MMM D, YYYY") : "-"}</div>
                {remainingDays !== null && <span className="text-base font-normal">{remainingDays} days left</span>}
              </div>
            </div>
          </div>

          {canManageBilling && (
            <Card className="mb-4">
              <div className="font-semibold text-left text-lg mb-3">Plan Management</div>
              <div className="flex flex-col md:flex-row gap-2">
                <Button
                  className="bg-primary text-white"
                  onClick={() =>
                    singleLocationPlan &&
                    setPendingAction({
                      type: "changePlan",
                      planId: singleLocationPlan.id,
                      amountDollars: singleLocationPlan?.amount_decimal,
                    })
                  }
                >
                  Change to Single Location
                </Button>
                <Button
                  className="bg-primary text-white"
                  onClick={() => setMultiLocationModalOpen(true)}
                >
                  Change to Multi-Location
                </Button>
                <Button
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={() => setPendingAction({ type: "cancel", immediate: false })}
                >
                  Cancel Subscription
                </Button>
              </div>
            </Card>
          )}

          <Card className="mb-4">
            <div className="font-semibold text-left text-lg mb-3">Billing Admins</div>
            <p className="text-sm text-gray-600 text-left mb-3">
              The Account Owner always has billing authority. Billing Admins can change the plan, cancel the
              subscription, and manage other Billing Admins.
            </p>
            <ul className="text-left mb-3 space-y-2">
              {(subData?.billingAdmins || []).map((admin) => (
                <li key={admin.uuid} className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <span>
                    {admin.name}{" "}
                    <span className="text-gray-500 text-sm">
                      ({admin.email}, {admin.isOwner ? "Owner" : admin.role})
                    </span>
                  </span>
                  {canManageBilling && !admin.isOwner && (
                    <Button
                      className="bg-gray-200 text-red-700 text-sm px-3 py-1"
                      onClick={() => setPendingAction({ type: "revoke", targetUserUuid: admin.uuid, label: admin.name })}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {canManageBilling && (
              <div className="flex flex-col md:flex-row gap-2">
                <select
                  className="border rounded-md px-3 py-2 text-sm flex-1"
                  value={addAdminTarget}
                  onChange={(e) => setAddAdminTarget(e.target.value)}
                >
                  <option value="">Select a Director or Manager to add as Billing Admin...</option>
                  {eligibleStaff.map((u) => (
                    <option key={u.uuid} value={u.uuid}>
                      {u.first_name} {u.last_name} ({u.role})
                    </option>
                  ))}
                </select>
                <Button
                  className="bg-primary text-white"
                  disabled={!addAdminTarget}
                  onClick={() => setPendingAction({ type: "grant", targetUserUuid: addAdminTarget })}
                >
                  Add Billing Admin
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <div className="font-semibold text-left text-lg mb-3">Billing History</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border rounded-md">
                <thead>
                  <tr className="bg-[#FFA944] text-left">
                    <th className="px-4 py-2 font-semibold">Invoice Date</th>
                    <th className="px-4 py-2 font-semibold">Amount</th>
                    <th className="px-4 py-2 font-semibold">Plan Type</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(subData?.billingHistory || []).map((bill, i) => (
                    <tr key={i} className="border-t text-left">
                      <td className="px-4 py-2">
                        {bill.hosted_invoice_url ? (
                          <a href={bill.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {moment.utc(bill.invoiceDate).format("MMM D, YYYY")}
                          </a>
                        ) : (
                          moment.utc(bill.invoiceDate).format("MMM D, YYYY")
                        )}
                      </td>
                      <td className="px-4 py-2">${bill.amount}</td>
                      <td className="px-4 py-2">{bill.planName}</td>
                      <td className="px-4 py-2">
                        <Badge variant={bill.status === "paid" ? "success" : "warning"}>{bill.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {userRole === "super_admin" &&
        (transactions ? <SubscriptionTable transactions={transactions} /> : <div>No record found.</div>)}

      <RestaurantSelectModal
        isOpen={isRestaurantModalOpen}
        onClose={() => setIsRestaurantModalOpen(false)}
        restaurants={restaurants}
        selectedRestaurant={selectedRestaurant}
        onSelect={(uuid) => {
          setSelectedRestaurant(uuid);
          setIsRestaurantModalOpen(false);
        }}
      />

      {/* Multi-Location Modal */}
      <Modal isOpen={multiLocationModalOpen} onClose={() => setMultiLocationModalOpen(false)}>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Select Number of Locations</h2>
          <input
            type="number"
            min={2}
            max={6}
            value={multiLocationCount}
            onChange={(e) => setMultiLocationCount(Number(e.target.value))}
            className="border rounded px-2 py-1 w-24 text-lg font-bold mb-4"
          />
          <div className="mb-4">
            Total: <span className="font-bold">${calculateMultiLocationPrice(multiLocationCount).toFixed(2)}/month</span>
          </div>
          <div className="flex gap-2">
            <Button
              className="bg-primary text-white flex-1"
              onClick={() => {
                setMultiLocationModalOpen(false);
                if (!multiLocationPlan) return;
                const amountDollars = calculateMultiLocationPrice(multiLocationCount).toFixed(2);
                if (hasActiveSubscription) {
                  setPendingAction({
                    type: "changePlan",
                    planId: multiLocationPlan?.product?.id || multiLocationPlan?.id,
                    amountDollars,
                  });
                } else {
                  handleStartCheckout(multiLocationPlan?.product?.id || multiLocationPlan?.id, amountDollars, false);
                }
              }}
            >
              Confirm
            </Button>
            <Button className="bg-gray-300 flex-1" onClick={() => setMultiLocationModalOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Re-auth confirmation for every high-risk action */}
      <ReauthModal
        isOpen={pendingAction?.type === "cancel"}
        title="Cancel Subscription"
        description="Are you sure you want to cancel this subscription? You will lose Unit 2 access at the end of the current billing period."
        confirmLabel="Cancel Subscription"
        confirmClassName="!bg-red-600 hover:!bg-red-700"
        onCancel={() => setPendingAction(null)}
        onConfirm={runPendingAction}
        submitting={actionSubmitting}
      />
      <ReauthModal
        isOpen={pendingAction?.type === "changePlan"}
        title="Change Plan"
        description="This will change your subscription plan immediately and may result in a prorated charge."
        confirmLabel="Confirm Plan Change"
        onCancel={() => setPendingAction(null)}
        onConfirm={runPendingAction}
        submitting={actionSubmitting}
      />
      <ReauthModal
        isOpen={pendingAction?.type === "grant"}
        title="Add Billing Admin"
        description="This person will be able to change the plan, cancel the subscription, and manage other Billing Admins."
        confirmLabel="Add Billing Admin"
        onCancel={() => setPendingAction(null)}
        onConfirm={runPendingAction}
        submitting={actionSubmitting}
      />
      <ReauthModal
        isOpen={pendingAction?.type === "revoke"}
        title="Remove Billing Admin"
        description={pendingAction?.label ? `Remove billing authority from ${pendingAction.label}?` : undefined}
        confirmLabel="Remove"
        confirmClassName="!bg-red-600 hover:!bg-red-700"
        onCancel={() => setPendingAction(null)}
        onConfirm={runPendingAction}
        submitting={actionSubmitting}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
